/**
 * Host-keyed slash-command registry.
 *
 * The user-facing slash list is fetched per channel from the backend
 * (``GET /api/v1/skills/slash-commands?channel=…``) and cached in this
 * module. Per-command UX rules that need functions (auto-send predicates)
 * are merged in from channel-local ``SlashCommandExtras`` maps under
 * ``integrations/tools/<channel>/slashCommands.ts``.
 *
 * Call ``loadCommandsForHost(host, apiToken)`` once when the host is
 * detected (and the auth token is available). After that the synchronous
 * lookups (``getCommandsForHost`` / ``filterCommands`` / ``lookupCommand``)
 * read from the in-memory cache — they return an empty list when the host
 * hasn't been loaded yet, so the menu simply never opens.
 *
 * The cache expires after ``SLASH_COMMANDS_CACHE_TTL_MS`` (AIVA-7670): a
 * persona's enabled-skill list can change in Studio at any time, and without
 * an expiry an already-open task pane would keep serving a skill the admin
 * just disabled for the rest of the session. This mirrors the AI backend's
 * own persona-skill cache window (``persona_cache_ttl_seconds``, currently
 * 60s) so a Studio toggle is reflected here on roughly the same timescale.
 */
import { fetchSlashCommands, type SlashCommandDTO } from "../services/api";
import type { RefreshTokenFn } from "../../shared/api/fetchWithRefresh";
import type { Channel } from "../../shared/host";
import { getDisabledSkillKeys } from "../../shared/userSkillPrefs";
import { DISCOVERY_COMMANDS, DISCOVERY_SLASHES } from "./discovery";
import { getLocalCommandsForHost, isLocalCommand } from "./localCommands";
import { rankSlashMatches, usesMatchingLadder } from "./matching";
import { WORD_SLASH_EXTRAS } from "../../integrations/tools/word/slashCommands";
import { OUTLOOK_SLASH_EXTRAS } from "../../../integrations/tools/outlook/slashCommands";
import type { SlashCommand, SlashCommandExtras, SlashCommandInputRequirements } from "./types";

const EMPTY_COMMANDS: readonly SlashCommand[] = Object.freeze([]);

// Per-host map of "slash → behaviour extras". Channels that don't appear
// here get no extras for any slash (pure prefill behaviour, no fallback).
const EXTRAS_BY_HOST: Readonly<Partial<Record<Channel, Readonly<Record<string, SlashCommandExtras>>>>> = {
  word: WORD_SLASH_EXTRAS,
  outlook: OUTLOOK_SLASH_EXTRAS,
};

// In-memory cache populated by ``loadCommandsForHost``. Module-scoped on
// purpose — the slash menu is shared across components, so a single load
// per host is enough.
const _cache = new Map<Channel, readonly SlashCommand[]>();
const _inflight = new Map<Channel, Promise<readonly SlashCommand[]>>();
const _loadedAt = new Map<Channel, number>();

/** How long a loaded list is considered fresh before the next call re-fetches it. */
export const SLASH_COMMANDS_CACHE_TTL_MS = 60_000;

// The backend strips the leading "/" from `slash_command` before returning
// the DTO (skills.py: ``slash=skill.slash_command.lstrip("/")``), but the
// per-host extras maps and every caller of ``lookupCommand`` work with the
// full ``/slash`` form. Normalise here so the cached SlashCommand.slash
// always includes the leading "/" — otherwise typed slashes like
// ``/meeting to discuss …`` would never match a registered command and
// would fall through to raw text in the bubble (AIVA-5057 / 5074).
function normaliseSlash(slash: string): string {
  return slash.startsWith("/") ? slash.toLowerCase() : `/${slash.toLowerCase()}`;
}

function normaliseInputRequirements(
  dto: SlashCommandDTO,
): SlashCommandInputRequirements | undefined {
  const raw = dto.inputRequirements;
  if (!raw) return undefined;
  const requirements: SlashCommandInputRequirements = {
    ...(raw.requiresWordSelection ? { requiresWordSelection: true } : {}),
    ...(raw.requiresMicrosoft365 ? { requiresMicrosoft365: true } : {}),
    ...(raw.requiresSubjectOrSelection ? { requiresSubjectOrSelection: true } : {}),
    ...(raw.requiresSlideTextSelection ? { requiresSlideTextSelection: true } : {}),
    ...(raw.nudgeIfMissing ? { nudgeIfMissing: raw.nudgeIfMissing } : {}),
  };
  return Object.keys(requirements).length > 0 ? requirements : undefined;
}

function mergeExtras(host: Channel, dto: SlashCommandDTO): SlashCommand {
  const slash = normaliseSlash(dto.slash);
  const extras = EXTRAS_BY_HOST[host]?.[slash];
  const inputRequirements = normaliseInputRequirements(dto);
  return {
    slash,
    skillKey: dto.skillKey,
    label: dto.label,
    description: dto.description,
    hint: dto.hint,
    prompt: dto.prompt,
    category: dto.category,
    autoApply: dto.autoApply,
    ...(extras?.autoSendWhen ? { autoSendWhen: extras.autoSendWhen } : {}),
    ...(extras?.autoSendMessage ? { autoSendMessage: extras.autoSendMessage } : {}),
    ...(extras?.echoSlashInBubble ? { echoSlashInBubble: true } : {}),
    ...(inputRequirements ? { inputRequirements } : {}),
  };
}

/**
 * Fetch and cache the slash-command set for ``host``. Idempotent while the
 * cache is fresh — repeat calls return the cached list; concurrent calls
 * share one in-flight request. The cache auto-expires after
 * ``SLASH_COMMANDS_CACHE_TTL_MS``, and ``invalidateCommandCache`` can also
 * force an immediate re-fetch.
 */
export async function loadCommandsForHost(
  host: Channel,
  apiToken: string | null,
  refreshTokens?: RefreshTokenFn,
): Promise<readonly SlashCommand[]> {
  const cached = _cache.get(host);
  const loadedAt = _loadedAt.get(host);
  const isFresh = loadedAt !== undefined && Date.now() - loadedAt < SLASH_COMMANDS_CACHE_TTL_MS;
  // Callers (useCommandList) render the resolved value directly, so it must
  // carry the discovery slashes exactly as getCommandsForHost does — including
  // when the skill fetch returns nothing.
  if (cached && isFresh) return getCommandsForHost(host);
  const pending = _inflight.get(host);
  if (pending) return pending;

  const promise = (async () => {
    const dtos = await fetchSlashCommands(host, apiToken, refreshTokens);
    const commands = Object.freeze(dtos.map((d) => mergeExtras(host, d)));
    // Only cache non-empty results. An empty array almost always means a
    // transient backend issue (cache hasn't seen the latest seed; skill
    // docs missing the channels field) — caching it would lock the menu
    // closed until page reload. Re-fetch on the next host/token change.
    if (commands.length > 0) {
      _cache.set(host, commands);
      _loadedAt.set(host, Date.now());
    } else {
      console.warn(
        `[slash-commands] Backend returned 0 entries for channel="${host}". ` +
          "Check the skill docs have `channels` + `slashCommand` set, and that the API cache is fresh (restart the backend after reseed).",
      );
    }
    return getCommandsForHost(host);
  })();

  _inflight.set(host, promise);
  try {
    return await promise;
  } finally {
    _inflight.delete(host);
  }
}

/** Drop the cached commands for a host (or all hosts). Tests + sign-out call this. */
export function invalidateCommandCache(host?: Channel): void {
  if (host) {
    _cache.delete(host);
    _inflight.delete(host);
    _loadedAt.delete(host);
  } else {
    _cache.clear();
    _inflight.clear();
    _loadedAt.clear();
  }
}

/** Test-only: prime the cache without hitting the network. */
export function _setCommandsForHostForTesting(host: Channel, commands: readonly SlashCommand[]): void {
  _cache.set(host, Object.freeze([...commands]));
  _loadedAt.set(host, Date.now());
}

/**
 * Returns the slash commands currently cached for ``host``, plus the
 * discovery slashes.
 *
 * Skills come from the backend cache and are empty until loaded (the menu
 * simply never opens). The discovery slashes are appended unconditionally —
 * they are answered locally, so they work with no network and must be
 * offerable even when the skill fetch failed. They are excluded from
 * ``lookupCommand`` and from the ``/?`` listing itself; see ``discovery.ts``.
 */
export function getCommandsForHost(host: Channel | null): readonly SlashCommand[] {
  if (host === null) return EMPTY_COMMANDS;
  const skills = _cache.get(host);
  // Local commands (e.g. SC-06's /rephrase) are answered in the add-in, so they're offered even without the skill fetch.
  const local = getLocalCommandsForHost(host);
  if (!skills) return local.length ? [...local, ...DISCOVERY_COMMANDS] : DISCOVERY_COMMANDS;
  return [...withoutUserDisabled(host, skills), ...local, ...DISCOVERY_COMMANDS];
}

/**
 * Subtract the user's disabled skills (Settings > Skills, ST-04). Read fresh
 * from storage on every call — not memoised — so a toggle flipped in Settings
 * takes effect on the very next keystroke without a cache invalidation hook.
 * This is the single choke point for the `/?` listing (filterCommands), typed
 * slashes (lookupCommand) and forced-skill lookups (findCommandBySkillKey),
 * which all read through the two functions above/below.
 */
function withoutUserDisabled(host: Channel, skills: readonly SlashCommand[]): readonly SlashCommand[] {
  const disabled = getDisabledSkillKeys(host);
  if (disabled.size === 0) return skills;
  return skills.filter((cmd) => !disabled.has(cmd.skillKey));
}

/** The host's skill commands only — no discovery slashes. */
export function getSkillCommandsForHost(host: Channel | null): readonly SlashCommand[] {
  if (host === null) return EMPTY_COMMANDS;
  const skills = _cache.get(host);
  if (!skills) return EMPTY_COMMANDS;
  return withoutUserDisabled(host, skills);
}

/**
 * Resolve a slash token (e.g. ``"/compose"``) against the host's registry.
 * Returns null when the token isn't a registered command — the caller should
 * fall through to natural-language entry rather than failing.
 *
 * Discovery slashes resolve to null on purpose: they carry no skill, so the
 * turn must fall through to the local listing rather than being routed.
 *
 * On ladder hosts (PowerPoint, ST-07) the token also matches a skill's name or
 * id, exactly or as a substring — see ``matching.ts``. A local command is that
 * host's own explicit shortcut, so it resolves to null there too rather than
 * letting a partial match on some skill's name take it over.
 */
export function lookupCommand(host: Channel | null, slash: string): SlashCommand | null {
  const normalized = slash.toLowerCase();
  if (DISCOVERY_SLASHES.has(normalized)) return null;
  if (usesMatchingLadder(host)) {
    if (isLocalCommand(host, normalized)) return null;
    return rankSlashMatches(getSkillCommandsForHost(host), normalized)[0] ?? null;
  }
  for (const cmd of getSkillCommandsForHost(host)) {
    if (cmd.slash === normalized) return cmd;
  }
  return null;
}

/**
 * Resolve a slash command by its backend skill key (e.g. ``"word_improve"``).
 * Used when the caller has a ``forced_skill_key`` in hand — set by a pill or
 * an explicit slash — and needs the skill's declared UX metadata (input
 * requirements, hint, etc.). Returns null when the host isn't loaded yet or
 * no command in the cache maps to that skill.
 */
export function findCommandBySkillKey(host: Channel | null, skillKey: string): SlashCommand | null {
  // Skill commands only — discovery slashes carry an empty skillKey, so
  // including them here would let a lookup for "" match one of them.
  for (const cmd of getSkillCommandsForHost(host)) {
    if (cmd.skillKey === skillKey) return cmd;
  }
  return null;
}

/**
 * Filter the host's commands by a prefix (the typed slash) — used to
 * narrow the menu as the user types. ``"/c"`` matches ``/catchup``,
 * ``/compose``, etc. An exact match returns just that command.
 */
export function filterCommands(host: Channel | null, prefix: string): readonly SlashCommand[] {
  const normalized = prefix.toLowerCase();
  if (normalized === "" || normalized === "/") return getCommandsForHost(host);
  return getCommandsForHost(host).filter((cmd) => cmd.slash.startsWith(normalized));
}
